/**
 * Slide generation logic for the PowerPoint Builder.
 *
 * Slide model:
 *   - One "section" slide per chunk (dramatic, chunk title only)
 *   - One "content" slide per chunk with bits as bullet points
 *     (skipped if the chunk has no bit titles)
 *   - Optional "pause" and "transition" slides per chunk
 *   - Plus title / objective / agenda / qa / closing
 *   - Plus user-inserted "visual" slides (mostly full-bleed)
 *
 * Hardened against PowerPoint repair prompts by:
 *   - Aggressively sanitizing every string before passing to addText
 *   - Replacing problem unicode chars with safe equivalents
 *   - Skipping shapes / text with empty content
 *   - Keeping every shape comfortably inside the slide bounds
 *   - Validating every color value
 */

import { NSP_LOGO_DATA_URI, NSP_LOGO_ASPECT } from './nspLogo.js';

export const SLIDE_TYPES = {
  title:     { label: 'Title Slide' },
  objective: { label: 'Objective' },
  agenda:    { label: 'Agenda' },
  section:   { label: 'Section Title' },
  content:   { label: 'Content' },
  visual:    { label: 'Visual' },
  pause:     { label: 'Pause & Process' },
  qa:        { label: 'Questions & Answers' },
  closing:   { label: 'Closing' },
};

// =====================================================================
// TEMPLATES — fonts and colors. All fonts ship with PowerPoint on
// both Mac and Windows so users see the right look regardless of OS.
//
// Optional per-template footer behaviour:
//   - footerStyle: 'stripes' (default) draws the two thin accent stripes.
//                  'logoBar' draws a solid coloured bar with a logo image.
//   - footerBarColor: hex for the bar (logoBar only).
//   - footerLogo: data URI of the logo to place in the bar (logoBar only).
//   - footerLogoAspect: width/height of that logo, so it never distorts.
//   - restricted: optional string. If set, this template is only offered
//                 to users whose email ends with that value. The Builder
//                 UI filters on it; see Builder.jsx. (Defence-in-depth:
//                 the gate lives in the UI because that's where the user's
//                 identity is known.)
// =====================================================================
export const TEMPLATES = [
  {
    id: 'lab-default',
    name: 'Presentation Lab',
    desc: 'The original — forest + terra',
    headingFont: 'Century Gothic',
    bodyFont: 'Calibri',
    bgDark: '1b2340', bgLight: 'ffffff',
    titleColorDark: 'ffffff', titleColorLight: '1c1a16',
    bodyColorDark: 'cccccc', bodyColorLight: '3d3830',
    labelColorDark: '888899', labelColorLight: '7d7568',
    accentLeft: '3a8a63', accentRight: 'c45c3a',
  },
  {
    id: 'executive-navy',
    name: 'Executive Navy',
    desc: 'Crisp, corporate, confident',
    headingFont: 'Georgia',
    bodyFont: 'Calibri',
    bgDark: '0f1d3a', bgLight: 'ffffff',
    titleColorDark: 'ffffff', titleColorLight: '1a2b4a',
    bodyColorDark: 'd6d6e0', bodyColorLight: '333947',
    labelColorDark: '8a92a8', labelColorLight: '6b7280',
    accentLeft: '1a2b4a', accentRight: 'c9a961',
  },
  {
    id: 'warm-editorial',
    name: 'Warm Editorial',
    desc: 'Magazine feel, serif headers',
    headingFont: 'Georgia',
    bodyFont: 'Cambria',
    bgDark: '2a1f1a', bgLight: 'f7f2e9',
    titleColorDark: 'f7f2e9', titleColorLight: '2a2a2a',
    bodyColorDark: 'd9c9b3', bodyColorLight: '4a3f30',
    labelColorDark: '8b7d6a', labelColorLight: '8b3a2f',
    accentLeft: '8b3a2f', accentRight: 'c9a961',
  },
  {
    id: 'modern-geometric',
    name: 'Modern Geometric',
    desc: 'Clean, contemporary, minimal',
    headingFont: 'Bahnschrift',
    bodyFont: 'Calibri Light',
    bgDark: '161618', bgLight: 'fafafa',
    titleColorDark: 'ffffff', titleColorLight: '111111',
    bodyColorDark: 'b8b8bd', bodyColorLight: '4a4a4a',
    labelColorDark: '6e6e75', labelColorLight: '888888',
    accentLeft: 'ff4d4d', accentRight: '111111',
  },
  {
    id: 'tech-mono',
    name: 'Tech Mono',
    desc: 'Terminal vibes, code-friendly',
    headingFont: 'Consolas',
    bodyFont: 'Calibri',
    bgDark: '0d1117', bgLight: 'f6f8fa',
    titleColorDark: '7ee787', titleColorLight: '0d1117',
    bodyColorDark: 'c9d1d9', bodyColorLight: '24292f',
    labelColorDark: '8b949e', labelColorLight: '6e7781',
    accentLeft: '7ee787', accentRight: '58a6ff',
  },
  {
    id: 'playful-handwritten',
    name: 'Playful Handwritten',
    desc: 'Friendly, approachable, warm',
    headingFont: 'Bradley Hand',
    bodyFont: 'Trebuchet MS',
    bgDark: '2d2435', bgLight: 'fff9f0',
    titleColorDark: 'ffd166', titleColorLight: '5b2c6f',
    bodyColorDark: 'f4e4c1', bodyColorLight: '4a3f55',
    labelColorDark: 'a896b8', labelColorLight: '9a8aa8',
    accentLeft: 'ef476f', accentRight: 'ffd166',
  },
  {
    // Nova Scotia Power — white slides with the NSP blue footer bar and
    // the white NSP logo bottom-left, mirroring NSP's own deck template.
    // Restricted to @nspower.ca users (gated in Builder.jsx).
    //
    // Light theme is the intended look (white background). The dark-theme
    // values are sensible fallbacks so the template still renders if a
    // user happens to have the dark toggle on, but NSP decks are meant to
    // be light.
    id: 'nspower',
    name: 'Nova Scotia Power',
    desc: 'White slides · NSP blue footer + logo',
    headingFont: 'Arial',
    bodyFont: 'Calibri',
    bgDark: '0f2747', bgLight: 'ffffff',
    titleColorDark: 'ffffff', titleColorLight: '004580',
    bodyColorDark: 'd6e0ee', bodyColorLight: '333947',
    labelColorDark: '8aa0c0', labelColorLight: '6b7280',
    accentLeft: '1b5eac', accentRight: '56b146',
    // Footer: solid NSP blue bar with the white logo instead of stripes.
    footerStyle: 'logoBar',
    footerBarColor: '1b5eac',
    footerLogo: NSP_LOGO_DATA_URI,
    footerLogoAspect: NSP_LOGO_ASPECT,
    restricted: '@nspower.ca',
  },
];

export function getTemplate(id) {
  return TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];
}

// =====================================================================
// SAFETY HELPERS — these are the keys to stopping the repair prompt.
// =====================================================================

// Replace problematic unicode characters with safer ASCII equivalents,
// strip illegal XML control chars, and normalize line endings.
function safeText(s) {
  if (s === null || s === undefined) return '';
  let out = String(s);

  // Replace specific characters that have caused PowerPoint XML issues
  out = out
    .replace(/\u2248/g, '~')          // ≈ approximately
    .replace(/\u2192/g, '->')         // → right arrow
    .replace(/\u2190/g, '<-')         // ← left arrow
    .replace(/\u2014/g, '--')         // — em dash
    .replace(/\u2013/g, '-')          // – en dash
    .replace(/\u2018/g, "'")          // ' left single quote
    .replace(/\u2019/g, "'")          // ' right single quote
    .replace(/\u201C/g, '"')          // " left double quote
    .replace(/\u201D/g, '"')          // " right double quote
    .replace(/\u2026/g, '...')        // … ellipsis
    .replace(/\u00B7/g, '-')          // · middle dot
    .replace(/\u2022/g, '-');         // • bullet

  // Strip illegal XML control characters (keep tab, newline, CR)
  out = out.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  // Normalize line endings
  out = out.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Strip leading / trailing whitespace
  return out.trim();
}

function hasContent(s) {
  return typeof s === 'string' && s.trim().length > 0;
}

function safeColor(hex, fallback = '000000') {
  if (typeof hex !== 'string') return fallback;
  const c = hex.replace(/^#/, '').toLowerCase();
  return /^[0-9a-f]{6}$/.test(c) ? c : fallback;
}

// =====================================================================
// SLIDE GENERATION
// =====================================================================

/**
 * Build a deck from the user's data.
 *
 * Returns an array of slides in display order. User-inserted slides
 * (data.builder.insertedSlides) are spliced in immediately after whichever
 * auto-generated slide they were inserted "after" (by id).
 */
export function generateSlides(data) {
  const auto = generateAutoSlides(data);
  const inserted = data?.builder?.insertedSlides ?? [];
  return weaveInsertedSlides(auto, inserted);
}

/**
 * The auto-generated portion of the deck — derived purely from
 * logistics, objective, and design data. No user-inserted slides here.
 */
function generateAutoSlides(data) {
  const slides = [];
  const objective = data.objective?.built;
  const objText = objective
    ? `${objective.prefix} ${objective.verb} ${objective.task}.`
    : '';
  const chunks = data.design?.chunks ?? [];
  const title = data.logistics?.title?.trim() || '';

  slides.push({
    id: 'title',
    type: 'title',
    title: title || "Make your title teach -- what's the one idea?",
    body: 'Your name | Your role | Date',
    notes:
      "Make your title teach. If the audience only remembers the title, will they walk away with something useful?",
    chunk: '',
  });

  slides.push({
    id: 'objective',
    type: 'objective',
    title: 'Presentation Objective',
    body: objText || 'A single sentence describing what your audience will be able to do by the end.',
    notes:
      "Show this slide early. It tells the audience exactly what they will be able to do -- not what you will cover.",
    chunk: '',
  });

  slides.push({
    id: 'agenda',
    type: 'agenda',
    title: 'Agenda',
    body:
      chunks.length > 0
        ? chunks.map((c, i) => `- ${c.title || `Topic ${i + 1}`}`).join('\n')
        : '- Chunk 1\n- Chunk 2\n- Chunk 3',
    notes:
      "Each chunk title should make its main point clear so the audience can hold onto it without their notes.",
    chunk: '',
  });

  chunks.forEach((chunk, i) => {
    const num = String(i + 1);
    const chunkTitle = chunk.title || `Topic ${num}`;

    // Section title slide — chunk title only, dramatic.
    slides.push({
      id: `section-${chunk.id}`,
      type: 'section',
      title: chunkTitle,
      body: '',
      notes:
        "A reset for the audience and a moment for you. Make eye contact, breathe, let the transition land.",
      chunk: num,
      chunkTitle,
    });

    // Content slide — chunk title small + bits as bullets, IF there are
    // any non-empty bits. If the chunk has no bit content, skip the
    // content slide entirely (the section title alone is enough).
    const bits = (chunk.subtopics ?? [])
      .map((sub) => ({
        title: (sub.title ?? '').trim(),
        dictation: (sub.dictation ?? sub.body ?? '').trim(),
        time: sub.time,
      }))
      .filter((b) => b.title || b.dictation);

    if (bits.length > 0) {
      // Body: one bullet per bit title. Bits with no title fall back
      // to a leading snippet of dictation so they aren't invisible.
      const bulletLines = bits.map((b) => {
        const headline = b.title || firstSentence(b.dictation);
        return `- ${headline}`;
      });

      // Speaker notes: each bit's title as a heading, with its dictation
      // underneath. Times are noted next to the heading. Bits with no
      // dictation are still listed so the structure is preserved.
      const noteSections = bits.map((b) => {
        const heading = b.title || '(no title)';
        const timeStr = b.time ? ` (~${b.time} min)` : '';
        const dictation = b.dictation ? `\n${b.dictation}` : '';
        return `${heading}${timeStr}${dictation}`;
      });
      const notes = noteSections.join('\n\n---\n\n');

      slides.push({
        id: `content-${chunk.id}`,
        type: 'content',
        title: chunkTitle,
        body: bulletLines.join('\n'),
        notes,
        chunk: num,
        chunkTitle,
      });
    }

    if (chunk.discussion?.open && (chunk.discussion.type || chunk.discussion.prompt)) {
      slides.push({
        id: `pause-${chunk.id}`,
        type: 'pause',
        title: chunk.discussion.type || 'Discussion',
        body: chunk.discussion.prompt || '',
        notes: chunk.discussion.time ? `~${chunk.discussion.time} min` : '',
        chunk: num,
        chunkTitle,
      });
    }

    if (chunk.transition && chunk.transition.trim()) {
      slides.push({
        id: `transition-${chunk.id}`,
        type: 'content',
        title: 'Transition',
        body: chunk.transition,
        notes: '',
        chunk: num,
        chunkTitle,
      });
    }
  });

  slides.push({
    id: 'qa',
    type: 'qa',
    title: 'Questions & Answers',
    body: "Before we close, what questions or reactions do you have?",
    notes:
      "Take Q and A before your closing -- that way you control the last words your audience hears.",
    chunk: '',
  });

  slides.push({
    id: 'closing',
    type: 'closing',
    title: 'The one thing you want them to remember',
    body: objText
      ? `${objText}\n\nThe single most important takeaway`
      : 'Restate your objective. Land on your single most important point. No new information.',
    notes:
      "Recency effect -- what you say here has disproportionate staying power. Restate the objective. Then your single most important point.",
    chunk: '',
  });

  return slides;
}

/**
 * Splice user-inserted slides into the auto-generated deck.
 *
 * Each inserted slide has an `insertAfterSlideId`. We walk the auto
 * slides in order, and after each one we append any inserted slides
 * whose `insertAfterSlideId` matches. Inserted slides whose anchor
 * no longer exists (because the user changed Design and the anchor
 * disappeared) are appended to the end so they aren't lost.
 */
function weaveInsertedSlides(autoSlides, insertedSlides) {
  if (insertedSlides.length === 0) return autoSlides;

  const byAnchor = new Map();
  for (const s of insertedSlides) {
    const key = s.insertAfterSlideId || '__end__';
    if (!byAnchor.has(key)) byAnchor.set(key, []);
    byAnchor.get(key).push(s);
  }

  const out = [];
  const validAnchors = new Set(autoSlides.map((s) => s.id));

  for (const auto of autoSlides) {
    out.push(auto);
    const after = byAnchor.get(auto.id);
    if (after) out.push(...after);
  }

  // Orphaned inserted slides (anchor no longer exists) → append to end
  for (const s of insertedSlides) {
    if (s.insertAfterSlideId && !validAnchors.has(s.insertAfterSlideId)) {
      out.push(s);
    }
  }
  // Inserted slides explicitly anchored to "__end__" (none currently
  // produced, but supported for future flexibility)
  const atEnd = byAnchor.get('__end__');
  if (atEnd) out.push(...atEnd);

  return out;
}

function firstSentence(text) {
  if (!text) return '';
  const trimmed = text.trim();
  const match = trimmed.match(/^[^.!?\n]{1,80}/);
  return (match ? match[0] : trimmed.slice(0, 80)).trim();
}

// =====================================================================
// PPTX EXPORT — heavily defensive
// =====================================================================
export async function downloadPptx(
  slides,
  theme = 'dark',
  filename = 'presentation.pptx',
  templateId = 'lab-default'
) {
  const mod = await import('pptxgenjs');
  const PptxGenJS = mod.default || mod;

  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_WIDE'; // 13.333 x 7.5 in
  pres.title = 'The Presentation Lab';
  pres.company = 'McEachern and Associates Consulting Inc.';

  const tpl = getTemplate(templateId);
  const isDark = theme === 'dark';

  const bg         = safeColor(isDark ? tpl.bgDark         : tpl.bgLight,         isDark ? '1b2340' : 'ffffff');
  const titleColor = safeColor(isDark ? tpl.titleColorDark : tpl.titleColorLight, isDark ? 'ffffff' : '1c1a16');
  const bodyColor  = safeColor(isDark ? tpl.bodyColorDark  : tpl.bodyColorLight,  isDark ? 'cccccc' : '3d3830');
  const labelColor = safeColor(isDark ? tpl.labelColorDark : tpl.labelColorLight, isDark ? '888899' : '7d7568');
  const accentL    = safeColor(tpl.accentLeft,  '3a8a63');
  const accentR    = safeColor(tpl.accentRight, 'c45c3a');

  // Footer config — most templates use the two thin accent stripes.
  // The NSP template uses a solid coloured bar with a logo instead.
  const footer = {
    style: tpl.footerStyle === 'logoBar' ? 'logoBar' : 'stripes',
    barColor: safeColor(tpl.footerBarColor, '1b5eac'),
    logo: typeof tpl.footerLogo === 'string' ? tpl.footerLogo : null,
    logoAspect:
      typeof tpl.footerLogoAspect === 'number' && tpl.footerLogoAspect > 0
        ? tpl.footerLogoAspect
        : 3.1,
  };

  // Slide dimensions for LAYOUT_WIDE (in inches). Keep all shapes
  // inside these bounds with margin to spare.
  const SLIDE_W = 13.333;
  const SLIDE_H = 7.5;

  // Type-specific font sizes.
  //   - Title slide: the biggest — splashy opener.
  //   - Section slides: large, dramatic chapter break.
  //   - All other slides: a single uniform size so the deck feels
  //     consistent (no random title-size variation between objective,
  //     agenda, content, pause, qa, and closing).
  const TITLE_SIZE_TITLE   = 72;  // opening title slide — biggest
  const TITLE_SIZE_SECTION = 60;  // chunk break — second biggest
  const TITLE_SIZE_DEFAULT = 40;  // every other slide, uniform
  const BODY_SIZE_BULLETS  = 24;  // bit bullets on content slides
  const BODY_SIZE_DEFAULT  = 28;  // body on objective / pause / etc.

  // Validate fonts (fall back if PptxGenJS dislikes them)
  const safeFont = (f, fb = 'Calibri') => (typeof f === 'string' && f.length > 0 ? f : fb);
  const headingFont = safeFont(tpl.headingFont, 'Century Gothic');
  const bodyFont    = safeFont(tpl.bodyFont, 'Calibri');

  slides.forEach((slide, slideIdx) => {
    try {
      const s = pres.addSlide();
      s.background = { color: bg };

      const slideType = slide.type || 'content';

      // ========================================================
      // VISUAL SLIDE — mostly full-bleed; just a chunk label and
      // a placeholder area where the user drops an image / chart.
      // ========================================================
      if (slideType === 'visual') {
        // Small chunk label in the corner
        const chunkText = safeText(slide.chunk);
        if (hasContent(chunkText)) {
          const chunkTitle = safeText(slide.chunkTitle);
          const label = hasContent(chunkTitle)
            ? `CHUNK ${chunkText} | ${chunkTitle.toUpperCase()}`
            : `CHUNK ${chunkText}`;
          const labelSafe = safeText(label);
          if (hasContent(labelSafe)) {
            s.addText(labelSafe, {
              x: 0.5, y: 0.3, w: 12, h: 0.3,
              fontSize: 10,
              fontFace: bodyFont,
              color: labelColor,
              charSpacing: 4,
            });
          }
        }

        // Placeholder hint inside a soft outlined area. The whole
        // shape is sized to fill most of the slide so the user can
        // delete the placeholder text and drop in a visual.
        const placeholderText = safeText(slide.body) || 'Replace this with a photo, chart, or diagram';
        s.addShape('rect', {
          x: 0.6, y: 0.85, w: 12.13, h: 6.2,
          fill: { color: bg },
          line: { color: labelColor, width: 1, dashType: 'dash' },
        });
        if (hasContent(placeholderText)) {
          s.addText(placeholderText, {
            x: 0.6, y: 0.85, w: 12.13, h: 6.2,
            fontSize: 18,
            fontFace: bodyFont,
            color: labelColor,
            italic: true,
            align: 'center',
            valign: 'middle',
          });
        }

        // Speaker notes
        const notesText = safeText(slide.notes);
        if (hasContent(notesText)) s.addNotes(notesText);

        // Footer
        addFooter(s, footer, accentL, accentR);
        return;
      }

      // ========================================================
      // SECTION SLIDE — dramatic chunk title, vertically centered.
      // ========================================================
      if (slideType === 'section') {
        // "Section X" eyebrow
        const num = safeText(slide.chunk);
        if (hasContent(num)) {
          s.addText(`SECTION ${num}`, {
            x: 0.5, y: 2.3, w: 12.3, h: 0.5,
            fontSize: 14,
            fontFace: bodyFont,
            color: labelColor,
            charSpacing: 6,
            align: 'center',
          });
        }

        // Big centered chunk title
        const titleText = safeText(slide.title);
        if (hasContent(titleText)) {
          s.addText(titleText, {
            x: 0.5, y: 2.9, w: 12.3, h: 2.4,
            fontSize: TITLE_SIZE_SECTION,
            bold: true,
            fontFace: headingFont,
            color: titleColor,
            align: 'center',
            valign: 'middle',
          });
        }

        // Speaker notes
        const notesText = safeText(slide.notes);
        if (hasContent(notesText)) s.addNotes(notesText);

        addFooter(s, footer, accentL, accentR);
        return;
      }

      // ========================================================
      // ALL OTHER TYPES — title at top + body below.
      // Includes title / objective / agenda / content / pause /
      // qa / closing. Only the font sizes vary.
      // ========================================================

      // Top labels: chunk label (left) + slide-type label (right)
      const chunkText = safeText(slide.chunk);
      if (hasContent(chunkText)) {
        const chunkTitle = safeText(slide.chunkTitle);
        const label = hasContent(chunkTitle)
          ? `CHUNK ${chunkText} | ${chunkTitle.toUpperCase()}`
          : `CHUNK ${chunkText}`;
        const labelSafe = safeText(label);
        if (hasContent(labelSafe)) {
          s.addText(labelSafe, {
            x: 0.5, y: 0.35, w: 9, h: 0.3,
            fontSize: 11,
            fontFace: bodyFont,
            color: labelColor,
            charSpacing: 4,
          });
        }
      }

      const typeLabel = (SLIDE_TYPES[slideType] || SLIDE_TYPES.content).label;
      const typeLabelSafe = safeText(typeLabel.toUpperCase());
      if (hasContent(typeLabelSafe)) {
        s.addText(typeLabelSafe, {
          x: 9.5, y: 0.35, w: 3.3, h: 0.3,
          fontSize: 9,
          fontFace: bodyFont,
          color: labelColor,
          align: 'right',
          charSpacing: 4,
        });
      }

      // Title — title slide is splashy, all other (non-section) slides
      // share one uniform size.
      const titleText = safeText(slide.title);
      let titleSize = TITLE_SIZE_DEFAULT;
      let titleH = 1.4;
      if (slideType === 'title') {
        titleSize = TITLE_SIZE_TITLE;
        titleH = 2.6;
      } else if (slideType === 'content') {
        // Content slides need a slightly tighter title block to leave
        // room for bullets, but keep the same font size as everything
        // else for visual consistency.
        titleH = 1.1;
      }

      if (hasContent(titleText)) {
        s.addText(titleText, {
          x: 0.5, y: 1.0, w: 12.3, h: titleH,
          fontSize: titleSize,
          bold: true,
          fontFace: headingFont,
          color: titleColor,
          valign: 'top',
        });
      }

      // Body
      const bodyText = safeText(slide.body);
      if (hasContent(bodyText)) {
        const bodySize =
          slideType === 'content' ? BODY_SIZE_BULLETS : BODY_SIZE_DEFAULT;

        // Content slides render as a real bulleted list. Other slides
        // render as plain text (so e.g. the title slide's name/date
        // line isn't bulleted).
        const bodyY = 1.0 + titleH + 0.3;
        const bodyH = SLIDE_H - bodyY - 1.0;

        if (slideType === 'content' || slideType === 'agenda') {
          // Split on newlines and addText as paragraphs with bullets.
          const lines = bodyText
            .split('\n')
            .map((l) => l.replace(/^\s*[-•·]\s*/, '').trim())
            .filter(Boolean);
          if (lines.length > 0) {
            const paragraphs = lines.map((line, i) => ({
              text: line,
              options: { bullet: { type: 'bullet' }, breakLine: i < lines.length - 1 },
            }));
            s.addText(paragraphs, {
              x: 0.5, y: bodyY, w: 12.3, h: bodyH,
              fontSize: bodySize,
              fontFace: bodyFont,
              color: bodyColor,
              valign: 'top',
              lineSpacingMultiple: 1.3,
              paraSpaceAfter: 8,
            });
          }
        } else {
          s.addText(bodyText, {
            x: 0.5, y: bodyY, w: 12.3, h: bodyH,
            fontSize: bodySize,
            fontFace: bodyFont,
            color: bodyColor,
            valign: 'top',
            lineSpacingMultiple: 1.25,
          });
        }
      }

      // Speaker notes
      const notesText = safeText(slide.notes);
      if (hasContent(notesText)) s.addNotes(notesText);

      // Footer
      addFooter(s, footer, accentL, accentR);
    } catch (err) {
      // If a single slide fails, log it and continue with the rest
      console.error(`Error generating slide ${slideIdx}:`, err, slide);
    }
  });

  const safeFilename = safeText(filename) || 'presentation.pptx';
  await pres.writeFile({ fileName: safeFilename });
}

// =====================================================================
// FOOTER
// =====================================================================
// Most templates: two thin accent stripes along the bottom.
// NSP template (footerStyle 'logoBar'): a solid coloured bar with the
// white logo bottom-left and a small page-number-ish spacer at right.
// =====================================================================
function addFooter(s, footer, accentL, accentR) {
  if (footer.style === 'logoBar' && footer.logo) {
    addLogoBar(s, footer);
  } else {
    addAccentStripes(s, accentL, accentR);
  }
}

// Single rectangle each, sitting comfortably inside slide bounds.
function addAccentStripes(s, accentL, accentR) {
  const stripeY = 7.40;
  const stripeH = 0.06;
  s.addShape('rect', {
    x: 0.05, y: stripeY, w: 6.6, h: stripeH,
    fill: { color: accentL },
    line: { type: 'none' },
  });
  s.addShape('rect', {
    x: 6.7, y: stripeY, w: 6.6, h: stripeH,
    fill: { color: accentR },
    line: { type: 'none' },
  });
}

// Solid NSP-blue bar across the bottom with the white logo bottom-left.
// Bar height and logo size are tuned to echo NSP's own template footer.
function addLogoBar(s, footer) {
  const SLIDE_W = 13.333;
  const SLIDE_H = 7.5;
  const barH = 0.78;
  const barY = SLIDE_H - barH;

  // The bar itself — full slide width.
  s.addShape('rect', {
    x: 0, y: barY, w: SLIDE_W, h: barH,
    fill: { color: footer.barColor },
    line: { type: 'none' },
  });

  // Logo, vertically centred in the bar, inset from the left edge.
  // Height is comfortably smaller than the bar so the whole logo —
  // including the "Nova Scotia" line and the top of the swoosh — sits
  // inside the blue with margin above and below. Width follows the
  // aspect ratio so the logo never distorts.
  const logoH = 0.42;
  const logoW = logoH * footer.logoAspect;
  const logoX = 0.45;
  const logoY = barY + (barH - logoH) / 2;
  s.addImage({
    data: footer.logo,
    x: logoX, y: logoY, w: logoW, h: logoH,
  });
}

// =====================================================================
// HELPERS for the Builder UI to manage user-inserted slides
// =====================================================================

let _insertedId = Date.now();
function genInsertedId() {
  _insertedId += 1;
  return `inserted-${_insertedId}`;
}

/** Build a fresh blank "visual" slide, anchored to the given slide id. */
export function makeVisualSlide(insertAfterSlideId) {
  return {
    id: genInsertedId(),
    type: 'visual',
    title: '',
    body: '',
    notes: '',
    chunk: '',
    chunkTitle: '',
    insertAfterSlideId,
  };
}

/** True if the slide is a user-inserted one (vs auto-generated). */
export function isInsertedSlide(slide) {
  return typeof slide?.id === 'string' && slide.id.startsWith('inserted-');
}
